'use client'

import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

import { AccountKeysPanel } from '@/components/generationApi/AccountKeysPanel'
import {
  GenerationApiAccessErrorPanel,
  GenerationApiAccessRequestPanel,
} from '@/components/generationApi/GenerationApiAccessRequestPanel'
import { GenerationApiKeysPanel, GenerationApiPageHeader } from '@/components/generationApi/GenerationApiKeysPanel'
import { Loader2 } from '@/components/ui/icons'
import { useAuth } from '@/hooks/useAuth'
import { useBillingInfo } from '@/hooks/useBillingInfo'
import { useGenerationApiAccess } from '@/hooks/useGenerationApiAccess'
import { isInTranslatorIframe } from '@/lib/i18n/inTranslatorIframe'

export default function MartiniApiPage() {
  const router = useRouter()
  const { auth, isLoading: authLoading, isAnonymous } = useAuth()
  const { billingInfo, isLoading: billingLoading } = useBillingInfo()
  const organizationId = billingInfo?.personalOrg?.id ?? null
  const apiAccess = useGenerationApiAccess(organizationId)

  useEffect(() => {
    if (isInTranslatorIframe()) return
    if (!authLoading && (!auth?.userId || isAnonymous)) {
      router.push('/projects')
    }
  }, [authLoading, auth?.userId, isAnonymous, router])

  const isLoading = authLoading || billingLoading || apiAccess.isLoading

  if (isLoading || !organizationId) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <GenerationApiPageHeader />
      {apiAccess.hasError ? (
        <GenerationApiAccessErrorPanel onRetry={apiAccess.retry} />
      ) : (
        <>
          {!apiAccess.enabled ? <GenerationApiAccessRequestPanel organizationId={organizationId} /> : null}
          {apiAccess.enabled || apiAccess.hasActiveKeys ? (
            <GenerationApiKeysPanel organizationId={organizationId} canCreate={apiAccess.enabled} canRevoke />
          ) : null}
        </>
      )}
      {/* Account keys need no API grant: the tool surface they use is open to every account. */}
      <AccountKeysPanel />
    </div>
  )
}
